import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CustomerChatButton from '../components/CustomerChatButton';
import { useAuth } from '../context/AuthContext';
import { Calendar, Clock, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const CustomerStatistics = () => {
  const { token, getAuthHeaders, runningUser, isCustomer } = useAuth();
  const navigate = useNavigate();
  
  const [statistics, setStatistics] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check if user has reports feature access (use runningUser for Run As support)
  const hasReportsFeature = runningUser?.plan_features?.reports === true;

  useEffect(() => {
    if (!isCustomer()) {
      navigate('/dashboard');
      return;
    }
    
    // Check if reports feature is enabled
    if (!hasReportsFeature) {
      navigate('/dashboard');
      return;
    }
    
    fetchStatistics();
  }, [runningUser, hasReportsFeature, isCustomer]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      
      // Fetch all appointments for this customer - use runningUser.id and getAuthHeaders for Run As support
      const response = await axios.get(
        `${API_BASE_URL}/appointments?customer_id=${runningUser.id}`,
        { headers: getAuthHeaders() }
      );

      const allAppointments = response.data.data || [];
      setAppointments(allAppointments);

      // Calculate statistics
      const now = new Date().toISOString();
      const completed = allAppointments.filter(apt => apt.start_time < now && apt.status === 'confirmed').length;
      const upcoming = allAppointments.filter(apt => apt.start_time >= now).length;
      const cancelled = allAppointments.filter(apt => apt.status === 'cancelled').length;
      const total = allAppointments.length;

      setStatistics({
        total,
        completed,
        upcoming,
        cancelled
      });
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Failed to load booking statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Booking Statistics</h1>
            <p className="text-gray-600 mt-2">View your appointment history and statistics</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Statistics Cards */}
          {statistics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <Calendar className="w-10 h-10 text-blue-500" />
                </div>
                <h3 className="text-3xl font-bold text-gray-800 mb-1">{statistics.total}</h3>
                <p className="text-gray-600">Total Bookings</p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-3xl font-bold text-gray-800 mb-1">{statistics.completed}</h3>
                <p className="text-gray-600">Completed</p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <Clock className="w-10 h-10 text-yellow-500" />
                </div>
                <h3 className="text-3xl font-bold text-gray-800 mb-1">{statistics.upcoming}</h3>
                <p className="text-gray-600">Upcoming</p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-3xl font-bold text-gray-800 mb-1">{statistics.cancelled}</h3>
                <p className="text-gray-600">Cancelled</p>
              </div>
            </div>
          )}

          {/* Booking History */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Booking History</h2>
            
            {appointments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Calendar</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {appointments.map((apt, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{formatDateTime(apt.start_time)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{apt.service_name || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{apt.calendar_name || 'N/A'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex text-xs px-2 py-1 rounded-full font-medium ${
                            apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            apt.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {apt.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No bookings yet. Book your first appointment!</p>
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
      
      {/* Floating Chat Button for Customers */}
      <CustomerChatButton />
    </div>
  );
};

export default CustomerStatistics;
